import React, { useState, useRef, useEffect } from 'react';
import { 
  Code2, Eye, Save, Share2, History, ChevronDown, 
  Maximize, Minimize, Plus, Loader2, Globe, ImageIcon, ExternalLink, Copy, Trash2
} from 'lucide-react';
import { type FileVersion, getVersionImageUrls, deleteVersionImageUrl } from '../../../../lib/apis_vibe';

interface EditorPanelProps {
  versions: FileVersion[];
  currentVersion: FileVersion | null;
  htmlCode: string;
  onCodeChange: (code: string) => void;
  onSaveCurrent: () => Promise<void>;   // 覆盖保存
  onCreateVersion: () => Promise<void>; // 新建版本
  onPublish: () => Promise<void>;       // 发布
  onChangeVersion: (versionId: number) => void;
  
  publishedUrl?: string | null;
  onShowPublishUrl?: () => void;
  versionId?: number;
}

const EditorPanel: React.FC<EditorPanelProps> = ({
  versions,
  currentVersion,
  htmlCode,
  onCodeChange,
  onSaveCurrent,
  onCreateVersion,
  onPublish,
  onChangeVersion,
  publishedUrl,
  onShowPublishUrl,
  versionId
}) => {
  const [activeTab, setActiveTab] = useState<'code' | 'preview' | 'resources'>('preview');
  const [isVersionMenuOpen, setIsVersionMenuOpen] = useState(false);
  const [isFullScreen, setIsFullScreen] = useState(false);
  
  const [isSavingCurrent, setIsSavingCurrent] = useState(false);
  const [isCreatingVersion, setIsCreatingVersion] = useState(false);
  const [isPublishing, setIsPublishing] = useState(false);

  const [imageUrls, setImageUrls] = useState<string[]>([]);
  const [isLoadingResources, setIsLoadingResources] = useState(false);
  const [contextMenu, setContextMenu] = useState<{ x: number; y: number; url: string } | null>(null);

  const previewContainerRef = useRef<HTMLDivElement>(null);
  const codeTextareaRef = useRef<HTMLTextAreaElement>(null);

  const toggleFullScreen = () => {
    if (!document.fullscreenElement) {
      if (activeTab === 'preview' && previewContainerRef.current) {
        previewContainerRef.current.requestFullscreen().then(() => {
          setIsFullScreen(true);
        }).catch(err => {
          console.error("全屏失败:", err);
          alert("无法进入全屏模式");
        });
      } else {
        alert("请先切换到预览模式");
      }
    } else {
      document.exitFullscreen().then(() => {
        setIsFullScreen(false);
      });
    }
  };

  useEffect(() => {
    const handleEsc = () => {
      if (!document.fullscreenElement) setIsFullScreen(false);
    };
    document.addEventListener('fullscreenchange', handleEsc);
    return () => document.removeEventListener('fullscreenchange', handleEsc);
  }, []);

  useEffect(() => {
    if (activeTab === 'resources' && versionId) {
      fetchImageUrls();
    }
  }, [activeTab, versionId]);

  useEffect(() => {
    const handleClick = () => setContextMenu(null);
    window.addEventListener('click', handleClick);
    return () => window.removeEventListener('click', handleClick);
  }, []);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 's') {
        if (activeTab === 'code' && document.activeElement === codeTextareaRef.current) {
          e.preventDefault();
          handleSaveClick();
        }
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [activeTab, htmlCode]);

  const fetchImageUrls = async () => {
    if (!versionId) return;
    setIsLoadingResources(true);
    try {
      const res = await getVersionImageUrls(versionId);
      const data = (res as any).data || res;
      setImageUrls(data.image_urls || []);
    } catch (err) {
      console.error('Failed to fetch image URLs:', err);
      setImageUrls([]);
    } finally {
      setIsLoadingResources(false);
    }
  };

  const handleContextMenu = (e: React.MouseEvent, url: string) => {
    e.preventDefault();
    setContextMenu({ x: e.clientX, y: e.clientY, url });
  };

  const handleCopyUrl = async (url: string) => {
    try {
      await navigator.clipboard.writeText(url);
      setContextMenu(null);
    } catch (err) {
      console.error('Copy failed:', err);
    }
  };

  const handleOpenInNewTab = (url: string) => {
    window.open(url, '_blank');
    setContextMenu(null);
  };

  const isImageUrl = (url: string) => {
    const lower = url.toLowerCase();
    return lower.includes('.jpg') || lower.includes('.jpeg') || lower.includes('.png') || lower.includes('.gif') || lower.includes('.webp') || lower.includes('.svg') || lower.includes('image');
  };

  const handleDeleteImage = async (url: string) => {
    if (!versionId) return;
    if (!confirm('确定要删除这个云资源文件吗？此操作不可恢复！')) return;
    
    try {
      await deleteVersionImageUrl(versionId, url);
      setImageUrls(prev => prev.filter(u => u !== url));
    } catch (err) {
      console.error('Delete image failed:', err);
      alert('删除失败，请稍后重试');
    }
  };

  // 1. 处理覆盖保存
  const handleSaveClick = async () => {
    setIsSavingCurrent(true);
    try {
      await onSaveCurrent();
    } finally {
      setIsSavingCurrent(false);
    }
  };

  // 2. 处理新建版本
  const handleCreateClick = async () => {
    setIsCreatingVersion(true);
    try {
      await onCreateVersion();
    } finally {
      setIsCreatingVersion(false);
    }
  };

  // 3. 处理发布
  const handlePublishClick = async () => {
    setIsPublishing(true);
    try {
      await onPublish();
    } finally {
      setIsPublishing(false);
    }
  };

  return (
    <div className="flex-1 flex flex-col bg-[#0f0f12] h-full relative overflow-hidden">
      
      {/* 顶部工具栏 */}
      <div className="h-14 border-b border-white/10 flex items-center justify-between px-6 bg-[#0f0f12] relative z-50 shrink-0">
        
        {/* 左侧：视图切换 */}
        <div className="flex bg-black/50 p-1 rounded-lg border border-white/10">
          <button 
            onClick={() => setActiveTab('preview')}
            className={`flex items-center gap-2 px-4 py-1.5 rounded-md text-xs font-bold transition-all ${
              activeTab === 'preview' ? 'bg-blue-600 text-white shadow-lg' : 'text-gray-400 hover:text-white'
            }`}
          >
            <Eye size={14} /> 预览
          </button>
          <button 
            onClick={() => setActiveTab('code')}
            className={`flex items-center gap-2 px-4 py-1.5 rounded-md text-xs font-bold transition-all ${
              activeTab === 'code' ? 'bg-purple-600 text-white shadow-lg' : 'text-gray-400 hover:text-white'
            }`}
          >
            <Code2 size={14} /> 代码
          </button>
          <button 
            onClick={() => setActiveTab('resources')}
            className={`flex items-center gap-2 px-4 py-1.5 rounded-md text-xs font-bold transition-all ${
              activeTab === 'resources' ? 'bg-green-600 text-white shadow-lg' : 'text-gray-400 hover:text-white'
            }`}
          >
            <ImageIcon size={14} /> 资源
          </button>
        </div>

        {/* 中间：版本时光机 */}
        <div className="flex items-center gap-3 bg-white/5 px-4 py-1.5 rounded-full border border-white/10 relative">
          <History size={14} className="text-gray-400" />
          <span className="text-xs text-gray-400 hidden md:inline">Time Travel</span>
          
          <div className="relative">
            <button 
              onClick={() => setIsVersionMenuOpen(!isVersionMenuOpen)}
              className="flex items-center gap-1 text-xs font-mono text-purple-400 hover:text-purple-300 focus:outline-none"
            >
              v{currentVersion?.version_number || '?'}
              <ChevronDown size={12} className={`transition-transform ${isVersionMenuOpen ? 'rotate-180' : ''}`} />
            </button>
            
            {isVersionMenuOpen && (
              <>
                <div className="fixed inset-0 z-40" onClick={() => setIsVersionMenuOpen(false)}></div>
                <div className="absolute top-full left-1/2 -translate-x-1/2 mt-2 w-48 bg-[#18181b] border border-white/10 rounded-xl shadow-xl overflow-hidden z-[100] animate-in fade-in zoom-in-95 duration-100">
                  <div className="max-h-60 overflow-y-auto py-1 custom-scrollbar">
                    {versions.map(v => (
                      <button
                        key={v.id}
                        onClick={() => {
                          onChangeVersion(v.id);
                          setIsVersionMenuOpen(false);
                        }}
                        className={`w-full text-left px-4 py-2.5 text-xs hover:bg-white/5 flex justify-between items-center group transition-colors ${
                          v.id === currentVersion?.id ? 'bg-purple-500/10 text-purple-300' : 'text-gray-400'
                        }`}
                      >
                        <span className="font-mono font-bold">v{v.version_number}</span>
                        <div className="text-right">
                          <div className="text-[10px] opacity-70 group-hover:opacity-100">
                            {new Date(v.created_at).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                          </div>
                          <div className="text-[9px] opacity-40 truncate max-w-[80px]">
                            {v.created_by === 'ai' ? '🤖 AI' : '👤 User'}
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              </>
            )}
          </div>
        </div>

        {/* 右侧：保存、发布、全屏 */}
        <div className="flex gap-2">
          
          {/* ✅ 1. 已发布链接 (如果有) */}
          {publishedUrl && (
            <button 
              onClick={onShowPublishUrl}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-600/20 text-blue-400 border border-blue-600/30 rounded-lg text-xs font-bold transition-colors hover:bg-blue-600 hover:text-white"
              title="查看已发布链接"
            >
              <Globe size={14} />
              已发布
            </button>
          )}

          {/* ✅ 2. 保存当前 (覆盖) */}
          <button 
            onClick={handleSaveClick}
            disabled={isSavingCurrent}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-700 hover:bg-gray-600 disabled:opacity-50 text-gray-200 rounded-lg text-xs font-bold transition-colors"
            title="覆盖保存到当前版本"
          >
            {isSavingCurrent ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
            保存
          </button>

          {/* ✅ 3. 新建版本 */}
          <button 
            onClick={handleCreateClick}
            disabled={isCreatingVersion}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-green-600 hover:bg-green-500 disabled:opacity-50 disabled:hover:bg-green-600 text-white rounded-lg text-xs font-bold transition-colors shadow-lg shadow-green-900/20 active:scale-95"
            title="提交为新版本 (Commit)"
          >
            {isCreatingVersion ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
            新建版本
          </button>

          {/* ✅ 4. 发布按钮 (Update Publish) */}
          <button 
            onClick={handlePublishClick}
            disabled={isPublishing}
            className="p-2 bg-white/5 text-gray-400 hover:text-white hover:bg-white/10 rounded-lg transition-colors relative group" 
            title="发布到云端 (Publish)"
          >
            {isPublishing ? <Loader2 size={16} className="animate-spin" /> : <Share2 size={16} />}
            {/* Tooltip */}
            <span className="absolute -bottom-8 left-1/2 -translate-x-1/2 bg-black text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none z-50">
              发布为网页
            </span>
          </button>

          {/* ✅ 5. 全屏按钮 */}
          <button 
            onClick={toggleFullScreen}
            className={`p-2 rounded-lg transition-colors ${
              activeTab === 'preview' ? 'bg-white/5 text-gray-400 hover:text-white hover:bg-white/10' : 'text-gray-600 cursor-not-allowed'
            }`}
            title={isFullScreen ? "退出全屏" : "网页全屏预览"}
            disabled={activeTab !== 'preview'}
          >
            {isFullScreen ? <Minimize size={16} /> : <Maximize size={16} />}
          </button>
        </div>
      </div>

      {/* 主内容区 */}
      <div className="flex-1 relative z-0 p-6 flex justify-center items-center overflow-hidden">
        
        <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.02)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.02)_1px,transparent_1px)] bg-[size:40px_40px] pointer-events-none"></div>

        {activeTab === 'preview' ? (
          <div 
            ref={previewContainerRef}
            className={`w-full h-full bg-white rounded-xl overflow-hidden shadow-2xl ring-1 ring-white/10 flex flex-col animate-in zoom-in-95 duration-200 ${isFullScreen ? 'fixed inset-0 z-[999] rounded-none' : ''}`}
          >
            {!isFullScreen && (
              <div className="h-8 bg-[#e4e4e7] flex items-center px-3 gap-2 shrink-0 border-b border-gray-300">
                <div className="flex gap-1.5">
                  <div className="w-2.5 h-2.5 rounded-full bg-red-500/80"></div>
                  <div className="w-2.5 h-2.5 rounded-full bg-yellow-500/80"></div>
                  <div className="w-2.5 h-2.5 rounded-full bg-green-500/80"></div>
                </div>
                <div className="flex-1 text-center">
                  <div className="bg-white/60 mx-auto w-1/2 h-5 rounded text-[10px] flex items-center justify-center text-gray-500 font-mono">
                    localhost:3000
                  </div>
                </div>
              </div>
            )}
            
            {isFullScreen && (
              <button 
                onClick={toggleFullScreen}
                className="absolute top-4 right-4 z-[1000] p-2 bg-black/50 text-white rounded-full hover:bg-black/70 transition-colors"
              >
                <Minimize size={20} />
              </button>
            )}

            <iframe 
              srcDoc={htmlCode}
              className="w-full h-full bg-white border-none"
              title="preview"
              sandbox="allow-scripts allow-modals allow-forms allow-same-origin allow-popups allow-downloads"
              allow="accelerometer; autoplay; camera; clipboard-read; clipboard-write; encrypted-media; fullscreen; geolocation; gyroscope; magnetometer; microphone; payment; picture-in-picture; usb; xr-spatial-tracking"
            />
          </div>
        ) : activeTab === 'code' ? (
          <div className="w-full h-full max-w-5xl bg-[#1e1e20] rounded-xl overflow-hidden shadow-2xl border border-white/10 font-mono text-sm flex animate-in slide-in-from-right-4 duration-200">
            <div className="w-12 bg-[#18181b] text-gray-600 text-right pr-3 py-4 select-none border-r border-white/5 font-mono text-xs leading-6 overflow-hidden">
              {htmlCode.split('\n').map((_, i) => (
                <div key={i}>{i + 1}</div>
              ))}
            </div>
            <textarea 
              ref={codeTextareaRef}
              value={htmlCode}
              onChange={(e) => onCodeChange(e.target.value)}
              className="flex-1 bg-transparent text-gray-300 p-4 resize-none focus:outline-none leading-6 selection:bg-blue-500/30 font-mono text-sm w-full h-full"
              spellCheck={false}
            />
          </div>
        ) : (
          <div className="w-full h-full max-w-5xl bg-[#1e1e20] rounded-xl overflow-hidden shadow-2xl border border-white/10 animate-in slide-in-from-right-4 duration-200 flex flex-col">
            <div className="p-4 border-b border-white/10 flex items-center justify-between">
              <h3 className="text-white font-bold flex items-center gap-2">
                <ImageIcon size={18} /> 云资源文件
              </h3>
              <span className="text-xs text-gray-500">共 {imageUrls.length} 个资源</span>
            </div>
            <div className="flex-1 overflow-y-auto p-4 custom-scrollbar">
              {isLoadingResources ? (
                <div className="flex items-center justify-center h-full">
                  <Loader2 size={24} className="animate-spin text-gray-400" />
                </div>
              ) : imageUrls.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-full text-gray-500">
                  <ImageIcon size={48} className="mb-4 opacity-30" />
                  <p>暂无云资源文件</p>
                  <p className="text-xs mt-2">在对话中生成的图片将显示在这里</p>
                </div>
              ) : (
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                  {imageUrls.map((url, idx) => (
                    <div 
                      key={idx} 
                      className="relative group cursor-pointer rounded-lg overflow-hidden border border-white/10 hover:border-green-500/50 transition-all bg-black/20"
                      onClick={() => handleOpenInNewTab(url)}
                      onContextMenu={(e) => handleContextMenu(e, url)}
                    >
                      {isImageUrl(url) ? (
                        <img 
                          src={url} 
                          alt={`resource-${idx}`}
                          className="w-full h-32 object-cover"
                          loading="lazy"
                        />
                      ) : (
                        <div className="w-full h-32 flex items-center justify-center bg-white/5">
                          <ExternalLink size={24} className="text-gray-400" />
                        </div>
                      )}
                      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors flex items-center justify-center">
                        <ExternalLink size={20} className="text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                      </div>
                      <button 
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteImage(url);
                        }}
                        className="absolute top-2 right-2 p-1.5 bg-red-500/80 hover:bg-red-500 text-white rounded-lg opacity-0 group-hover:opacity-100 transition-opacity z-10"
                        title="删除"
                      >
                        <Trash2 size={14} />
                      </button>
                      <div className="absolute bottom-0 left-0 right-0 bg-black/60 px-2 py-1 text-[10px] text-gray-300 truncate">
                        {url.split('/').pop()?.split('?')[0] || `资源 ${idx + 1}`}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {contextMenu && (
        <div 
          className="fixed bg-[#18181b] border border-white/10 rounded-lg shadow-xl py-1 z-[1000] min-w-[140px]"
          style={{ left: contextMenu.x, top: contextMenu.y }}
          onClick={(e) => e.stopPropagation()}
        >
          <button 
            onClick={() => handleCopyUrl(contextMenu.url)}
            className="w-full px-3 py-2 text-xs text-gray-300 hover:bg-white/5 flex items-center gap-2 transition-colors"
          >
            <Copy size={14} /> 复制链接
          </button>
          <button 
            onClick={() => handleOpenInNewTab(contextMenu.url)}
            className="w-full px-3 py-2 text-xs text-gray-300 hover:bg-white/5 flex items-center gap-2 transition-colors"
          >
            <ExternalLink size={14} /> 新窗口打开
          </button>
          <div className="h-px bg-white/10 my-1"></div>
          <button 
            onClick={() => {
              handleDeleteImage(contextMenu.url);
              setContextMenu(null);
            }}
            className="w-full px-3 py-2 text-xs text-red-400 hover:bg-red-500/10 flex items-center gap-2 transition-colors"
          >
            <Trash2 size={14} /> 删除
          </button>
        </div>
      )}
    </div>
  );
};

export default EditorPanel;
